import type {
  Block, Cell, ChipCell, Column, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Ledger & Reconciliation › Reconciler liveness (`lg-liveness`), read from
 * `GET /v1/admin/reconciliation/liveness` (go-wallet#386, handler
 * `internal/server/admin_ledger.go`).
 *
 * # This screen exists because a dead reconciler looks exactly like a clean one
 *
 * Every other screen in this section reports what reconciliation found. This one
 * reports whether reconciliation happened, and the two are not the same
 * question: a feed that is registered and never scheduled produces no findings,
 * and so does a book that balances. The whole surface between them is this
 * screen, and the way it fails is by rendering the first as the second.
 *
 * Three renderings are therefore forbidden here, each of them the shortest path
 * to a month of unwatched drift (go-wallet#88):
 *
 *   - **A feed that has never run must never acquire an age.** "0 minutes ago"
 *     beside four healthy reconcilers is the single most dangerous string this
 *     console can produce — it is the calmest possible rendering of a job nobody
 *     deployed. Zero runs is zero evidence, so a never-run feed reads `never
 *     ran` in words, in critical tone, and no age is shown for it even if the
 *     backend contradicts itself and sends one.
 *   - **A feed whose posture could not be determined is its own state.** Not
 *     healthy, and not never-run either. `ComposeLiveness` is explicit about
 *     this: when the run history will not read, every feed comes back
 *     `unavailable` with NO state at all, because reporting `never_ran` would
 *     send an operator to the deployment for a database error. The console keeps
 *     that separation and carries the backend's reason verbatim.
 *   - **An empty feed list is not "all feeds healthy".** The registry is what
 *     says which reconcilers are supposed to exist — it is passed to the backend
 *     separately from the history precisely so that a feed with no rows can
 *     still be listed. If it comes back with nothing in it, the thing that knows
 *     which feeds should exist did not answer, and the screen says so instead of
 *     drawing an empty table under a green heading.
 *
 * # It agrees with the Today board, because it does not re-derive the posture
 *
 * The state words and tones below are the same ones `dashboard.ts` renders, over
 * the same projection (`dashboard.ReconcilerStates`) served by two endpoints. An
 * operator who clicks a stale reconciler on the board must not arrive here and
 * read something else. The only thing this screen adds is the alarm, which the
 * board does not carry — and it adds it as a one-directional veto, never as a
 * new posture.
 *
 * That veto matters more than it looks. `reconcile.Assess` raises
 * `reconciler_never_produced_a_row` when a feed *expects* rows and has examined
 * none, while `dashboard.stateOf` reports `inert` when a feed does *not* expect
 * rows and has examined none — inverted predicates over the same condition. So
 * the feed that has run green all year and read an empty table every time can
 * arrive here as state `ok` carrying a `ticket` alarm, which is #88 in its exact
 * original shape. A raised alarm therefore takes green away from a row; nothing
 * ever grants it back.
 *
 * # No control, and there is not going to be one
 *
 * No re-run, no reset, no acknowledge, no snooze. A liveness alarm is fixed in
 * the deployment or in the feed, and anything this console offered would be a
 * button that makes the alarm quiet without making the reconciler run. Every
 * finding in this section resolves through an attributed compensating posting or
 * it does not resolve (invariants 2 and 9).
 *
 * # No money is on this screen at all
 *
 * The endpoint carries no amount, no currency and no balance — liveness is about
 * whether the checking happened, not about what it found. So there is no amount
 * cell, no `formatMinor`, and nothing here needs the ISO-4217 exponent. If a
 * figure of money ever appears in this payload it goes through `@/lib/money`
 * like every other one (invariants 3 and 4); it must not be added inline.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_ledger.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each. `ComposeLiveness` only ever emits two of the three
 * today, and the third is handled anyway — a console that crashes on a value the
 * contract permits is a console that breaks on the backend's next release.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

/** One raised liveness condition. `reconcile.Alarm`, on the wire. */
export interface ReconAlarmView {
  readonly kind: string
  /** `page` or `ticket`. A severity this console does not know outranks both. */
  readonly severity: string
  readonly detail: string
}

export interface FeedLivenessView {
  readonly feed: string
  readonly availability: string
  readonly reason?: string
  readonly provenance?: ProvenanceView

  /**
   * State and the timestamps are ABSENT when availability is not `available`.
   *
   * That is the backend's design and not an omission: a feed whose history could
   * not be read has no posture, and `never_ran` would be a claim nobody made.
   */
  readonly state?: string
  readonly last_success_at?: string
  readonly age_seconds?: number
  readonly last_error?: string
  readonly needs_attention: boolean

  /** Always present, `[]` when nothing is raised. Never omitted by the handler. */
  readonly alarms: readonly ReconAlarmView[]
}

/**
 * The whole registry, in one read.
 *
 * There is no `page` on this response, and that is deliberate rather than
 * missing: the list is the reconciler registry, which is a static, compiled-in
 * handful of feeds rather than a table that grows. So nothing here is truncated
 * and the screen says so out loud — the danger on a paginated list is a screen
 * that shows the first 50 of 4,000 and reads as the whole thing, and the way to
 * not have that danger is to state which of the two you are.
 */
export interface ReconLivenessResponse {
  readonly generated_at: string
  readonly feeds: readonly FeedLivenessView[]
}

export const RECON_LIVENESS_PATH = '/reconciliation/liveness'

const SOURCE = 'postgres · reconciliation_runs (GET /v1/admin/reconciliation/liveness)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `dashboard.ts` takes, and for the same reason: an
 * unrecognised value from the backend resolves to the reading that shows the
 * operator less confidence, never more. A future fourth state defaulting to
 * "available" would put an unverified posture on the one screen whose entire job
 * is to say whether anything was verified.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `The backend reported availability "${raw}", which this console does not know. Treated as unreadable.`
  }
  return 'This feed’s posture could not be read. This is not a report that it never ran.'
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a value past 2^53 has
 * already lost digits before any code here runs. An age is the one number on
 * this screen an operator acts on, and a plausible-looking wrong one is worse
 * than an absent one — returning `null` routes it into the unstated path rather
 * than printing a duration that is wrong in its last places.
 */
function exactInteger(value: number | undefined): number | null {
  if (value === undefined) return null
  return Number.isSafeInteger(value) ? value : null
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Returns the raw string unchanged rather than inventing one if the backend
 * sends something unparseable. A wrong timestamp beside a reconciler posture is
 * worse than an ugly one — it is the thing an operator quotes when they say the
 * feed ran.
 */
function stamp(iso: string | undefined): string {
  if (iso === undefined || iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/** Compact age, from the server's own arithmetic. No ambient clock here. */
function age(seconds: number): string {
  const s = Math.max(0, Math.trunc(seconds))
  if (s < 60) return `${String(s)}s`
  if (s < 3600) return `${String(Math.floor(s / 60))}m`
  if (s < 86400) return `${String(Math.floor(s / 3600))}h`
  return `${String(Math.floor(s / 86400))}d`
}

/**
 * The alarm list, defended against a payload that omitted it.
 *
 * The handler always sends `[]`, so the type says the field is there — but the
 * payload arrives as `unknown` and is asserted, not validated, so the one field
 * this file iterates is checked before it is iterated. A `TypeError` here would
 * take the screen to the error boundary and show an operator nothing at all,
 * which is the same outcome as an outage for a payload that was merely odd.
 */
function alarmsOf(f: FeedLivenessView): readonly ReconAlarmView[] {
  const raw: unknown = f.alarms
  return Array.isArray(raw) ? (raw as readonly ReconAlarmView[]) : []
}

// ---------------------------------------------------------------------------
// Posture — the same vocabulary the Today board renders
// ---------------------------------------------------------------------------

/**
 * A reconciler's posture, by state. Identical to `dashboard.ts` on purpose.
 *
 * This screen is the board's drill-down and both read the same projection, so a
 * feed that is amber on the board and green here would mean one of the two is
 * lying and an operator has no way to tell which. Copied rather than imported
 * because each adapter mirrors its own endpoint's wire shape (the house pattern
 * in `runs.ts`); the pairing is asserted in the tests instead.
 */
const STATE_TONE: Readonly<Record<string, Tone>> = {
  ok: 'g',
  stale: 'a',
  inert: 'a',
  failing: 'r',
  never_ran: 'r',
}

const STATE_POSTURE: Readonly<Record<string, string>> = {
  ok: 'within window',
  stale: 'stale',
  inert: 'inert',
  failing: 'failing',
  never_ran: 'unproven',
}

/**
 * What the posture means and where to look — the fix differs per state, and
 * collapsing them into "unhealthy" sends an operator to the wrong place every
 * second time (`internal/reconcile/liveness.go`).
 */
const STATE_NOTE: Readonly<Record<string, string>> = {
  ok: 'Succeeded inside its window.',
  stale: 'It has succeeded before, but not recently enough. The job stopped; the scheduler is where to look.',
  inert: 'It runs and succeeds and has never examined a single record. A feed reading an empty table looks identical to a feed with nothing to find.',
  failing: 'The job is running on schedule and its work is broken.',
  never_ran: 'Zero runs since the feed was registered. Never-run is not a clean run — this reconciler has proven nothing.',
}

const NEVER_RAN = 'never_ran'

// ---------------------------------------------------------------------------
// Alarms — severity is rendered, never flattened
// ---------------------------------------------------------------------------

const SEVERITY_TONE: Readonly<Record<string, Tone>> = { page: 'r', ticket: 'a' }

/**
 * How loudly a severity shouts, for picking the one the chip shows.
 *
 * An unknown severity ranks ABOVE `page`. It is the only ordering that cannot
 * hide anything: ranked at the bottom, a severity added to the backend after
 * this console was built would sit silently behind a ticket, and the first
 * anyone would know of it is the incident it was raised for.
 */
function severityRank(severity: string): number {
  if (severity === 'page') return 2
  if (severity === 'ticket') return 1
  return 3
}

function severityTone(severity: string): Tone {
  return SEVERITY_TONE[severity] ?? 'r'
}

function worstAlarm(alarms: readonly ReconAlarmView[]): ReconAlarmView | undefined {
  return [...alarms].sort((a, b) => severityRank(b.severity) - severityRank(a.severity))[0]
}

function severityLabel(severity: string): string {
  return severity === '' ? 'unnamed severity' : severity
}

/**
 * One alarm as a sentence, in the shape `reconcile.Alarm.String()` writes it.
 *
 * Every alarm names its OWN severity here even though the chip shows only the
 * worst. Two alarms of different severities collapsed into one colour is a
 * ticket hidden behind a page, and the detail column is where the operator finds
 * out there were two.
 */
function alarmSentence(a: ReconAlarmView): string {
  const kind = a.kind === '' ? 'unnamed alarm' : a.kind
  const detail = a.detail === '' ? 'no detail was given' : a.detail
  return `${kind} [${severityLabel(a.severity)}]: ${detail}`
}

// ---------------------------------------------------------------------------
// One feed, read
// ---------------------------------------------------------------------------

/**
 * How old the last success is, or why no age may be shown.
 *
 * Three outcomes rather than a nullable string, because "never" and "we could
 * not say" are different sentences and only one of them belongs in the screen's
 * missing list. Collapsing them is how a feed that legitimately has no age —
 * because it has never succeeded — ends up counted as an unread figure, and how
 * an unread figure ends up reading as a legitimate absence.
 */
type Age =
  | { readonly kind: 'shown'; readonly text: string }
  | { readonly kind: 'never'; readonly why: string }
  | { readonly kind: 'unstated'; readonly why: string }

function ageOf(f: FeedLivenessView, state: string): Age {
  // Never-run wins over anything in the age field, and this is the guard the
  // whole screen exists for. A feed the backend calls `never_ran` while also
  // sending an age is contradicting itself, and the safe half of the
  // contradiction is the one that shows no age: an operator who reads "0s" here
  // stops looking, and there is nothing behind them to catch it.
  if (state === NEVER_RAN) {
    const contradicted = f.age_seconds !== undefined
      || (f.last_success_at !== undefined && f.last_success_at !== '')
    return {
      kind: 'never',
      why: contradicted
        ? 'The backend also sent a last success or an age for a feed it reports as never having run. Neither is shown: zero runs is zero evidence, whatever the elapsed time is measured from.'
        : '',
    }
  }

  if (f.age_seconds === undefined) {
    // `failing` reaches here legitimately: a feed that has run and never once
    // succeeded has no success to age from, which is exactly what the state
    // already says. Any other state claims a successful run happened, so an
    // absent age is a figure the operator was not given.
    return state === 'failing'
      ? { kind: 'never', why: 'It has run and never once succeeded, so there is no successful run to measure from.' }
      : {
          kind: 'unstated',
          why: `The backend reported this feed as "${state === '' ? 'unnamed state' : state}" and sent no age, so how long ago it last succeeded is unknown — not recent.`,
        }
  }

  const seconds = exactInteger(f.age_seconds)
  if (seconds === null) {
    return {
      kind: 'unstated',
      why: 'The age arrived beyond exact integer range, so it cannot be stated. A duration that is wrong in its last places is worse than one that is absent.',
    }
  }
  return { kind: 'shown', text: age(seconds) }
}

interface Row {
  readonly feed: string
  readonly cells: readonly Cell[]
  readonly availability: Availability
  /** Empty unless a figure this row should have carried could not be stated. */
  readonly unstated: string
  readonly unproven: boolean
  readonly alarms: number
}

/**
 * One registered feed, as a table row.
 *
 * Tone is decided by three inputs that can only ever take green away: the state
 * mapping, the backend's own `needs_attention` flag, and whether an alarm is
 * raised. None of them can grant green to a row the state mapping did not
 * already call clean, which is what makes adding a fourth safe.
 */
function rowFor(f: FeedLivenessView): Row {
  const availability = availabilityOf(f.availability)

  if (availability !== 'available') {
    // No state, no age, no alarms — the backend sends none of them, and this
    // console must not fill any of them in. The reason is the whole content of
    // the row and it is carried verbatim.
    const reason = reasonFor(availability, f.availability, f.reason)
    const tone: Tone = availability === 'not_built' ? 'n' : 'r'
    const posture: ChipCell = availability === 'not_built'
      ? ['not assessed yet', 'n']
      : ['posture unknown', 'r']
    // Never a duration and never a bare dash. "unknown" is a different word from
    // "never" for the same reason the backend refuses to report `never_ran` when
    // the history will not read.
    const unknownAge: ChipCell = ['unknown', tone]
    // A feed nobody could assess raised no alarms, and rendering that as "none
    // raised" would be a clean bill of health issued by a read that never
    // happened.
    const notAssessed: ChipCell = ['not assessed', tone]

    return {
      feed: f.feed,
      availability,
      unstated: '',
      unproven: false,
      alarms: 0,
      cells: [f.feed, 'not known', unknownAge, posture, notAssessed, reason],
    }
  }

  const state = f.state ?? ''
  const known = Object.prototype.hasOwnProperty.call(STATE_TONE, state)
  const mapped: Tone = known ? (STATE_TONE[state] ?? 'r') : 'r'
  const alarms = alarmsOf(f)
  const worst = worstAlarm(alarms)
  const ago = ageOf(f, state)

  // No proven run vetoes green, whatever the backend called the state. A row
  // reading "within window" in the calm tone with no age behind it is the
  // reconciler that goes unlooked-at for a month (#88).
  const unproven = ago.kind !== 'shown'
  // And so does a raised alarm. `reconcile.Assess` and `dashboard.stateOf`
  // evaluate the never-produced-a-row condition on inverted predicates, so a
  // feed that has examined nothing since deployment can arrive here as `ok`
  // carrying a ticket. Green over that is the failure this screen is named for.
  const alarmTone: Tone | null = worst === undefined ? null : severityTone(worst.severity)

  let tone: Tone = mapped
  if (mapped === 'g' && alarmTone !== null) tone = alarmTone
  else if (mapped === 'g' && (f.needs_attention || unproven)) tone = 'a'

  const postureWord = known ? (STATE_POSTURE[state] ?? state) : (state === '' ? 'no state given' : state)
  const posture = mapped === 'g' && alarmTone !== null
    // The board's word is kept so the two screens name the same posture, and the
    // qualifier is added because the board cannot see the alarm.
    ? `${postureWord} · alarmed`
    : mapped === 'g' && unproven ? 'unproven' : postureWord

  const alarmChip: ChipCell = worst === undefined
    ? ['none raised', tone === 'g' ? 'g' : 'n']
    : [
        alarms.length === 1
          ? severityLabel(worst.severity)
          : `${String(alarms.length)} · ${severityLabel(worst.severity)}`,
        severityTone(worst.severity),
      ]

  const said: string[] = []
  if (!known) {
    said.push(`A reconciler state this console does not know${state === '' ? '' : ` ("${state}")`}. Treated as needing a human.`)
  } else {
    said.push(STATE_NOTE[state] ?? '')
  }
  if (ago.kind !== 'shown' && ago.why !== '') said.push(ago.why)
  if (f.last_error !== undefined && f.last_error !== '') said.push(`Last error: ${f.last_error}`)
  for (const a of alarms) said.push(alarmSentence(a))
  if (worst === undefined && mapped !== 'g' && known) {
    // The pair disagreeing is a backend condition worth naming rather than
    // smoothing over: the posture and the alarms are derived by two different
    // functions, and an operator who sees one without the other should know the
    // other was asked and said nothing.
    said.push('No alarm was raised against this posture. The two are derived separately, so this pair can disagree.')
  }
  if (f.needs_attention && mapped === 'g') {
    said.push('The backend flags this feed as needing a human even though its state reads clean.')
  }

  // In words, never as a duration, and never "0". `never ran` and `not stated`
  // are two different failures — one is a fact about the feed, the other is a
  // gap in what we were told — so they carry different words. Both are critical:
  // neither is evidence that anything was reconciled.
  const ageCell: ChipCell = ago.kind === 'shown'
    ? [ago.text, tone]
    : [ago.kind === 'never' ? 'never ran' : 'not stated', 'r']

  // Read from the timestamp, not from the age: a backend that sent a last
  // success it could not age would otherwise have the success erased from the
  // row, and "never" is the one word this column must not print wrongly. The
  // exception is a feed reported as never having run, where the timestamp is
  // suppressed for the same reason the age is — a date beside "never ran" is the
  // contradiction resolved in the direction that stops an operator looking.
  const at = state === NEVER_RAN ? '' : stamp(f.last_success_at)
  // And with no timestamp, "never" is itself a claim — one only the state may
  // make. A feed the backend calls `ok` while sending no last success has not
  // told us it never succeeded; it has told us nothing, and the two must not
  // print the same word.
  const lastSuccess = at !== '' ? at : (ago.kind === 'never' ? 'never' : 'not stated')
  const postureCell: ChipCell = [posture, tone]

  return {
    feed: f.feed,
    availability,
    unstated: ago.kind === 'unstated' ? ago.why : '',
    unproven: state === NEVER_RAN,
    alarms: alarms.length,
    cells: [
      f.feed,
      lastSuccess,
      ageCell,
      postureCell,
      alarmChip,
      said.filter((s) => s !== '').join(' · '),
    ],
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

const COLS: readonly Column[] = [
  ['Feed', 'x', 'minmax(150px,1.1fr)'],
  ['Last success', 'm', '168px'],
  ['Age', 'c', '128px'],
  ['Posture', 'c', '184px'],
  ['Alarm', 'c', '156px'],
  ['What it means', 'x', 'minmax(280px,2.2fr)'],
]

function footerFor(rows: readonly Row[]): string {
  const unproven = rows.filter((r) => r.unproven).length
  const unknown = rows.filter((r) => r.availability !== 'available').length
  const alarms = rows.reduce((n, r) => n + r.alarms, 0)
  return [
    `${String(rows.length)} registered feed(s)`,
    `${String(unproven)} never ran`,
    `${String(unknown)} posture unknown`,
    `${String(alarms)} alarm(s) raised`,
  ].join(' · ')
}

function tableFor(rows: readonly Row[]): TableBlock {
  return {
    t: 'table',
    title: 'Liveness per registered reconciler feed',
    src: 'ledger',
    note: 'The registry is the list of feeds that are SUPPOSED to run, held separately from the run history — which is the only way a feed nobody deployed can appear here at all.',
    // The corpus draws facets (`state=never_ran`, `stale=true`). This read
    // applies no filter, and a facet bar is a claim that one is applied.
    facets: false,
    cols: COLS,
    rows: rows.map((r) => r.cells),
    footer: footerFor(rows),
    foot2: 'Never-run is worded differently and carries no age — it is not “0 minutes ago”. This response is the whole registry: it has no page and nothing here is truncated.',
  }
}

function unprovenNote(rows: readonly Row[]): NoteBlock | null {
  const feeds = rows.filter((r) => r.unproven).map((r) => r.feed)
  if (feeds.length === 0) return null
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Unproven',
    title: feeds.length === 1
      ? `${feeds[0] ?? ''} has never produced a run`
      : `${String(feeds.length)} registered feeds have never produced a run`,
    body: `${feeds.join(', ')} — registered and never run. A reconciler that has never run has proven nothing: we do not know whether the drift it exists to catch is there. This is not the same as a clean run, and no age is shown for it because there is nothing to measure from.`,
    ref: 'reconciliation_runs · zero runs per feed · check the deployment and the schedule',
  }
}

function unknownPostureNote(rows: readonly Row[], response: ReconLivenessResponse): NoteBlock | null {
  const unknown = rows.filter((r) => r.availability !== 'available')
  if (unknown.length === 0) return null

  // The reason travels per feed and verbatim. "Unreadable" on its own tells an
  // operator nothing they can act on, and the difference between a feed nobody
  // has built and a history that will not read decides whether they page anyone.
  const reasons = unknown.map((r) => {
    const f = response.feeds.find((x) => x.feed === r.feed)
    const availability = availabilityOf(f?.availability ?? '')
    return `${r.feed}: ${reasonFor(availability, f?.availability ?? '', f?.reason)}`
  })

  return {
    t: 'note',
    tone: 'r',
    kicker: 'Posture unknown',
    title: unknown.length === 1
      ? `${unknown[0]?.feed ?? ''}’s posture could not be determined`
      : `${String(unknown.length)} feeds’ postures could not be determined`,
    body: `${reasons.join(' · ')} A feed whose history could not be read is neither healthy nor never-run — it is unknown, and somebody has to find out why the history will not read.`,
    ref: 'This is not a report that these feeds never ran.',
  }
}

/**
 * The screen when the registry itself came back empty.
 *
 * Rendered INSTEAD of the table, not above it. An empty table under the heading
 * "Liveness per registered reconciler feed" is read as five feeds with nothing
 * wrong, and the corpus's own empty state ("All reconcilers report a recent
 * success") would be an outright falsehood here: nothing reported anything. The
 * registry is compiled in and never legitimately empty, so an empty list means
 * the thing that knows which feeds should exist did not answer.
 */
function emptyRegistryNote(): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Registry',
    title: 'No reconciler feed was returned',
    body: 'The response listed no registered feed at all. The registry is what says which reconcilers are supposed to run, so with none listed this screen cannot say that any reconciler is healthy, stale or dead — it can only say that it does not know. This is not a clean board.',
    ref: 'reconcile.Feed registry · GET /v1/admin/reconciliation/liveness returned zero feeds',
  }
}

/** What the operator is not being told, for the screen's own provenance chip. */
function missingFrom(rows: readonly Row[]): string[] {
  const missing: string[] = []
  if (rows.length === 0) {
    missing.push('the reconciler registry (no feed was returned)')
  }
  for (const r of rows) {
    if (r.availability === 'not_built') missing.push(`${r.feed} posture (not built)`)
    else if (r.availability === 'unavailable') missing.push(`${r.feed} posture (could not be read)`)
    // A feed that has never succeeded is not a missing figure — that absence IS
    // the answer, and listing it would make the chip say `Partial` about the one
    // state this screen exists to report as a fact.
    else if (r.unstated !== '') missing.push(`${r.feed} last-success age (could not be stated)`)
  }
  return missing
}

function provenanceOf(response: ReconLivenessResponse, missing: readonly string[]): DataSource {
  const authorities = new Set<string>()
  for (const f of response.feeds) {
    if (f.provenance !== undefined) authorities.add(f.provenance.authority)
  }
  const authority = authorities.size === 0
    ? 'unattributed'
    : [...authorities].sort((a, b) => a.localeCompare(b)).join(' + ')
  const readAt = stamp(response.generated_at)

  return missing.length === 0
    ? { kind: 'live', source: SOURCE, authority, readAt }
    : { kind: 'partial', source: SOURCE, authority, readAt, missing }
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every posture, every availability and
 * every alarm path is table-testable without a client (CLAUDE.md Part III).
 */
export function composeLiveness(response: ReconLivenessResponse): ScreenData {
  const rows = response.feeds.map(rowFor)
  const missing = missingFrom(rows)
  const provenance = provenanceOf(response, missing)

  if (rows.length === 0) {
    return { blocks: [emptyRegistryNote()], provenance }
  }

  const blocks: Block[] = [tableFor(rows)]
  const unproven = unprovenNote(rows)
  if (unproven !== null) blocks.push(unproven)
  const unknown = unknownPostureNote(rows, response)
  if (unknown !== null) blocks.push(unknown)

  return { blocks, provenance }
}

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. Falling back to the design corpus would put five
 * invented postures — four of them green — on the screen an operator reads to
 * decide whether reconciliation is happening at all, which is the exact failure
 * the endpoint was shaped to prevent.
 *
 * `unread`, not `partial`: the chip renders one word, and "Partial" says some of
 * this is real. Nothing here is.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Reconciler liveness',
      title: 'This screen could not be read',
      body: `${reason} No feed’s posture, age or alarm below is known — that is different from every reconciler being healthy, and the screen shows none rather than guessing at any.`,
      ref,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isLivenessResponse(payload: unknown): payload is ReconLivenessResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<ReconLivenessResponse>).feeds)
}

/**
 * Reads reconciler liveness.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said "I
 * don't know" is a screen the operator can read, not an exception that collapses
 * to a stack trace. A refusal keeps its `decision_ref` (law 13) — the client
 * surfaces it and a screen must not swallow it.
 */
export const loadReconLiveness: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(RECON_LIVENESS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The reconciler liveness endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    // An AbortError is a navigation away, not a failure to report. Let it
    // propagate so the caller's cancellation stays a cancellation.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isLivenessResponse(payload)) {
    return unreadableScreen(
      'The reconciler liveness endpoint returned a shape this console does not know.',
      null,
    )
  }
  return composeLiveness(payload)
}
